import AgendaBackground from './modules/AgendaBackground.vue';
import BufferVideo from './modules/BufferVideo.vue';
import ButtonAndCardColor from './modules/ButtonAndCardColor.vue';
import CallToActionButton from './modules/CallToActionButton.vue';
import ChatExperience from './modules/ChatExperience.vue';
import EnterEventExperience from './modules/EnterEventExperience.vue';
import Favicon from './modules/Favicon.vue';
import FillerSlide from './modules/FillerSlide.vue';
import FontModule from './modules/FontModule/FontModule.vue';
import NewFontModule from './modules/FontModule/NewFontModule.vue';
import HeroBackground from './modules/HeroBackground.vue';
import Logo from './modules/Logo.vue';
import LowerThirds from './modules/LowerThirds.vue';
import NameCard from './modules/NameCard.vue';
import NameCardV2 from './modules/NameCardV2.vue';
import OnstageImages from './modules/OnstageImages.vue';
import PopupWindows from './modules/PopupWindows.vue';
import SelectBrandKitModule from './modules/SelectBrandKitModule.vue';
import SidebarColor from './modules/SidebarColor.vue';
import SocialMediaThumbnail from './modules/SocialMediaThumbnail.vue';
import SplashEnterEventExperience from './modules/SplashV2EnterEventExperience.vue';
import SplashHeroBackground from './modules/SplashV2HeroBackground.vue';
import StageBackground from './modules/StageBackground.vue';
import StageExperience from './modules/StageExperience.vue';
import TileBackground from './modules/TileBackground.vue';
import TrackingPixelSnippet from './modules/TrackingPixelSnippet.vue';
import VideoStreamBackground from './modules/VideoStreamBackground.vue';
import VirtualBackgrounds from './modules/VirtualBackgrounds.vue';
import BroadcastVideosStagePreview from './previews/BroadcastVideosStagePreview.vue';
import BufferVideoStagePreview from './previews/BufferVideoStagePreview.vue';
import OnboardingStagePreview from './previews/OnboardingStagePreview.vue';
import RoomsStagePreview from './previews/RoomsStagePreview.vue';
import SplashPageStageAgendaPreview from './previews/SplashPageStageAgendaPreview.vue';
import SplashPageStagePreview from './previews/SplashPageStagePreview.vue';
import TechCheckBrandingStagePreview from './previews/TechCheckBrandingStagePreview.vue';
import TextQnAPublishedStagePreview from './previews/TextQnAPublishedStagePreview.vue';
import VideosStageWithPopupPreview from './previews/VideosStageWithPopupPreview.vue';

const BLUE = 'rgba(0, 115, 255, 1)';
const TRANSPARENT_BLUE = 'rgba(0, 115, 255, 0.3)';
const TRANSPARENT = 'rgba(0, 0, 0, 0)';

export type Section = {
  id: string;
  label: string;
  description?: string;
  modules: Module[];
};

type Module = {
  id: string;
  component: any;
  preview?: {
    focus: keyof typeof previews;
    targets?: Target[];
    previews: (keyof typeof previews)[];
  };
};

type Target = {
  id: string;
  padding: number;
  stroke: string;
  fill: string;
  punch: boolean;
  linewidth: number;
  borderRadius: number;
};

export const previews = {
  splashPageStagePreview: { label: 'Event Landing', component: SplashPageStagePreview, ord: 0 },
  splashPageStageAgendaPreview: { label: 'Event Landing', component: SplashPageStageAgendaPreview, ord: 0 },
  broadcastVideosStagePreview: { label: 'Stage', component: BroadcastVideosStagePreview, ord: 1 },
  roomsStagePreview: { label: 'Event Tabs', component: RoomsStagePreview, ord: 2 },
  bufferVideoStagePreview: { label: 'Stage', component: BufferVideoStagePreview, ord: 1 },
  videosStageWithPopupPreview: { label: 'Stage', component: VideosStageWithPopupPreview, ord: 1 },
  textQnAPublishedStagePreview: { label: 'Stage', component: TextQnAPublishedStagePreview, ord: 1 },
  onboardingStagePreview: { label: 'Onboarding', component: OnboardingStagePreview, ord: 0 },
  techCheckBrandingStagePreview: { label: 'Tech Check', component: TechCheckBrandingStagePreview, ord: 0 }
};

const section1: Section = {
  id: 'brandingSettings',
  label: 'Global Settings',
  description: 'Select branding settings for your event registration page, landing page, and event space',
  modules: [
    {
      id: 'buttonAndCardColor',
      component: ButtonAndCardColor,
      preview: {
        focus: 'broadcastVideosStagePreview',
        targets: [
          {
            id: '[data-branding-target="text-qna-published"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 3,
            borderRadius: 5
          },
          {
            id: '[data-branding-target="secondarylarge"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 14
          },
          {
            id: '[data-branding-target="secondarymedium"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 9
          },
          {
            id: '[data-branding-target="secondarysmall"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 8
          },
          {
            id: '[data-branding-target="nav-hamburger"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 4
          },
          {
            id: '[data-branding-target="chat-send-button"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 4
          },
          {
            id: '[data-branding-target="primary-chattab-button"]:nth-child(1)',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 0
          },
          {
            id: '[data-branding-target="chattab-notification"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 0
          },
          {
            id: '[data-branding-target="sidebar-subtab-tab"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 0
          },
          {
            id: '[data-branding-target="broadcast-screen-saver"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 3,
            borderRadius: 5
          }
        ],
        previews: ['splashPageStagePreview', 'broadcastVideosStagePreview', 'roomsStagePreview']
      }
    },
    {
      id: 'logo',
      component: Logo,
      preview: {
        focus: 'broadcastVideosStagePreview',
        targets: [
          {
            id: '[data-branding-target="logo"]',
            padding: 3,
            stroke: BLUE,
            fill: TRANSPARENT_BLUE,
            punch: true,
            linewidth: 4,
            borderRadius: 4
          }
        ],
        previews: ['splashPageStagePreview', 'broadcastVideosStagePreview', 'roomsStagePreview']
      }
    },
    {
      id: 'Favicon',
      component: Favicon,
      preview: {
        // same as previous one so that scroll is not triggered
        focus: 'broadcastVideosStagePreview',
        previews: ['splashPageStagePreview', 'broadcastVideosStagePreview', 'roomsStagePreview']
      }
    },
    {
      id: 'socialMediaThumbnail',
      component: SocialMediaThumbnail,
      preview: {
        // same as previous one so that scroll is not triggered
        focus: 'broadcastVideosStagePreview',
        previews: ['splashPageStagePreview', 'broadcastVideosStagePreview', 'roomsStagePreview']
      }
    }
  ]
};

const section2: Section = {
  id: 'eventLanding',
  label: 'Entering The Event',
  description: "This section's settings will affect the event landing page and registration page.",
  modules: [
    {
      id: 'heroBackground',
      component: HeroBackground,
      preview: {
        focus: 'splashPageStagePreview',
        targets: [
          {
            id: '[data-branding-target="splash-hero"]',
            padding: 0,
            stroke: BLUE,
            fill: TRANSPARENT_BLUE,
            punch: false,
            linewidth: 3,
            borderRadius: 4
          }
        ],
        previews: ['splashPageStagePreview']
      }
    },
    {
      id: 'agendaBackground',
      component: AgendaBackground,
      preview: {
        focus: 'splashPageStageAgendaPreview',
        targets: [
          {
            id: '[data-branding-target="splash-background"]',
            padding: 0,
            stroke: BLUE,
            fill: TRANSPARENT_BLUE,
            punch: false,
            linewidth: 3,
            borderRadius: 4
          }
        ],
        previews: ['splashPageStageAgendaPreview']
      }
    },
    {
      id: 'landingFont',
      component: FontModule,
      preview: {
        focus: 'splashPageStageAgendaPreview',
        targets: [
          {
            id: '[data-branding-target="agenda-tile-date"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 4
          },
          {
            id: '[data-branding-target="agenda-item-title"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 4
          },
          {
            id: '[data-branding-target="agenda-item-description"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 4
          },
          {
            id: '[data-branding-target="event-details"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 4
          },
          {
            id: '[data-branding-target="entry-buttons"] > button',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 4
          },
          {
            id: '[data-branding-target="splash-title"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 4
          },
          {
            id: '[data-branding-target="splash-mobilescroll"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 4
          },
          {
            id: '[data-branding-target="card-title"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT_BLUE,
            punch: true,
            linewidth: 3,
            borderRadius: 4
          },
          {
            id: '[data-branding-target="bar-room-title"]',
            padding: 2,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 3,
            borderRadius: 4
          }
        ],
        previews: ['splashPageStagePreview', 'splashPageStageAgendaPreview']
      }
    },
    {
      id: 'tileBackground',
      component: TileBackground,
      preview: {
        focus: 'splashPageStageAgendaPreview',
        targets: [
          {
            id: '[data-branding-target="agenda-item"]',
            padding: 0,
            stroke: BLUE,
            fill: TRANSPARENT_BLUE,
            punch: false,
            linewidth: 3,
            borderRadius: 4
          }
        ],
        previews: ['splashPageStageAgendaPreview']
      }
    },
    {
      id: 'enterEventExperience',
      component: EnterEventExperience,
      preview: {
        focus: 'techCheckBrandingStagePreview',
        targets: [],
        previews: ['splashPageStagePreview', 'techCheckBrandingStagePreview', 'onboardingStagePreview']
      }
    },
    {
      id: 'trackingPixelSnippet',
      component: TrackingPixelSnippet,
      preview: {
        focus: 'splashPageStagePreview',
        targets: [],
        previews: ['splashPageStagePreview']
      }
    }
  ]
};

const section3: Section = {
  id: 'stage',
  label: 'Stage',
  modules: [
    {
      id: 'stageFont',
      component: FontModule,
      preview: {
        focus: 'roomsStagePreview',
        targets: [
          {
            id: '[data-branding-target="page-description"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 4
          },
          {
            id: '[data-branding-target="primary-text"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 4
          },
          {
            id: '[data-branding-target="chatmessagesholder"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 4
          },
          {
            id: '[data-branding-target="mobile-sidebar-controls"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 4
          },
          {
            id: '[data-branding-target="card-title"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT_BLUE,
            punch: true,
            linewidth: 3,
            borderRadius: 4
          },
          {
            id: '[data-branding-target="bar-room-title"]',
            padding: 2,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 3,
            borderRadius: 4
          },
          {
            id: '[data-branding-target="room-page-title"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 4
          }
        ],
        previews: ['broadcastVideosStagePreview', 'roomsStagePreview', 'onboardingStagePreview']
      }
    },
    {
      id: 'stageBackground',
      component: StageBackground,
      preview: {
        focus: 'roomsStagePreview',
        targets: [
          {
            id: '[data-branding-target="event-page"]',
            padding: -10,
            stroke: BLUE,
            fill: TRANSPARENT_BLUE,
            punch: true,
            linewidth: 6,
            borderRadius: 4
          }
        ],
        previews: ['broadcastVideosStagePreview', 'roomsStagePreview', 'onboardingStagePreview']
      }
    },
    {
      id: 'videoStreamBackground',
      component: VideoStreamBackground,
      preview: {
        focus: 'broadcastVideosStagePreview',
        targets: [
          {
            id: '[data-branding-target="streams-background"]',
            padding: -10,
            stroke: BLUE,
            fill: TRANSPARENT_BLUE,
            punch: true,
            linewidth: 6,
            borderRadius: 4
          }
        ],
        previews: ['broadcastVideosStagePreview']
      }
    },
    {
      id: 'PopupWindows',
      component: PopupWindows,
      preview: {
        focus: 'roomsStagePreview',
        targets: [
          {
            id: '[data-branding-target="user-dropdown"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 4
          },
          {
            id: '[data-branding-target="onboarding-popup"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 3,
            borderRadius: 5
          },
          {
            id: '[data-branding-target="profile-page"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 3,
            borderRadius: 5
          },
          {
            id: '[data-branding-target="pinned-chats-expanded"]',
            padding: 0,
            stroke: TRANSPARENT,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 4
          }
        ],
        previews: ['onboardingStagePreview', 'roomsStagePreview']
      }
    },
    {
      id: 'sidebarColor',
      component: SidebarColor,
      preview: {
        focus: 'roomsStagePreview',
        targets: [
          {
            id: '[data-branding-target="group-card"]',
            padding: 2,
            stroke: BLUE,
            fill: TRANSPARENT_BLUE,
            punch: true,
            linewidth: 3,
            borderRadius: 4
          },
          {
            id: '[data-branding-target="sidebar"]',
            padding: 4,
            stroke: BLUE,
            fill: TRANSPARENT_BLUE,
            punch: true,
            linewidth: 3,
            borderRadius: 4
          }
        ],
        previews: ['broadcastVideosStagePreview', 'roomsStagePreview']
      }
    },
    {
      id: 'callToActionButton',
      component: CallToActionButton,
      preview: {
        focus: 'videosStageWithPopupPreview',
        targets: [
          {
            id: '[data-branding-target="popup"]',
            padding: 4,
            stroke: BLUE,
            fill: TRANSPARENT_BLUE,
            punch: true,
            linewidth: 3,
            borderRadius: 4
          },
          {
            id: '[data-branding-target="demo-banner"]',
            padding: 4,
            stroke: BLUE,
            fill: TRANSPARENT_BLUE,
            punch: true,
            linewidth: 3,
            borderRadius: 4
          },
          {
            id: '[data-branding-target=demo-button] button',
            padding: 0,
            stroke: BLUE,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 0,
            borderRadius: 4
          }
        ],
        previews: ['videosStageWithPopupPreview']
      }
    },
    {
      id: 'bufferVideo',
      component: BufferVideo,
      preview: {
        focus: 'bufferVideoStagePreview',
        targets: [
          {
            id: '[data-branding-target="filler-slide"]',
            padding: 0,
            stroke: BLUE,
            fill: TRANSPARENT_BLUE,
            punch: true,
            linewidth: 3,
            borderRadius: 4
          }
        ],
        previews: ['bufferVideoStagePreview']
      }
    },
    {
      id: 'fillerSlide',
      component: FillerSlide,
      preview: {
        focus: 'bufferVideoStagePreview',
        targets: [
          {
            id: '[data-branding-target="filler-slide"]',
            padding: 0,
            stroke: BLUE,
            fill: TRANSPARENT_BLUE,
            punch: true,
            linewidth: 3,
            borderRadius: 4
          }
        ],
        previews: ['bufferVideoStagePreview']
      }
    },
    {
      id: 'stageExperience',
      component: StageExperience,
      preview: {
        focus: 'broadcastVideosStagePreview',
        targets: [],
        previews: ['broadcastVideosStagePreview']
      }
    }
  ]
};

const section4: Section = {
  id: 'videoProduction',
  label: 'Engagement & Communication',
  modules: [
    {
      id: 'chatExperience',
      component: ChatExperience,
      preview: {
        focus: 'broadcastVideosStagePreview',
        targets: [
          {
            id: '[data-branding-target="sidebar"]',
            padding: 0,
            stroke: BLUE,
            fill: TRANSPARENT_BLUE,
            punch: true,
            linewidth: 3,
            borderRadius: 4
          }
        ],
        previews: ['broadcastVideosStagePreview']
      }
    },
    {
      id: 'nameCard',
      component: NameCard,
      preview: {
        focus: 'broadcastVideosStagePreview',
        targets: [
          {
            id: '[data-branding-target="name-card"]',
            padding: 0,
            stroke: BLUE,
            fill: TRANSPARENT_BLUE,
            punch: true,
            linewidth: 3,
            borderRadius: 4
          }
        ],
        previews: ['broadcastVideosStagePreview']
      }
    },
    {
      id: 'virtualBackgrounds',
      component: VirtualBackgrounds,
      preview: {
        focus: 'broadcastVideosStagePreview',
        targets: [
          {
            id: '[data-branding-target="virtual-background"]',
            padding: 2,
            stroke: BLUE,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 3,
            borderRadius: 4
          }
        ],
        previews: ['broadcastVideosStagePreview']
      }
    },
    {
      id: 'logos',
      component: OnstageImages,
      preview: {
        focus: 'broadcastVideosStagePreview',
        targets: [
          {
            id: '[data-branding-target="stage-logo"]',
            padding: 2,
            stroke: BLUE,
            fill: TRANSPARENT,
            punch: true,
            linewidth: 3,
            borderRadius: 4
          }
        ],
        previews: ['broadcastVideosStagePreview']
      }
    },
    {
      id: 'lowerThirds',
      component: LowerThirds,
      preview: {
        focus: 'broadcastVideosStagePreview',
        targets: [
          {
            id: '[data-branding-target="lower-third"]',
            padding: 0,
            stroke: BLUE,
            fill: TRANSPARENT_BLUE,
            punch: true,
            linewidth: 3,
            borderRadius: 4
          }
        ],
        previews: ['broadcastVideosStagePreview']
      }
    }
  ]
};

export const newSplashSection: Section = {
  id: 'splashPage',
  label: 'Event Landing',
  description: "This section's settings will affect the event landing page and registration page.",
  modules: [
    {
      id: 'heroBackgroundV2',
      component: SplashHeroBackground,
      preview: {
        focus: 'splashPageStagePreview',
        targets: [
          {
            id: '[data-branding-target="splash-hero"]',
            padding: 0,
            stroke: BLUE,
            fill: TRANSPARENT_BLUE,
            punch: false,
            linewidth: 3,
            borderRadius: 4
          }
        ],
        previews: ['splashPageStagePreview']
      }
    },
    {
      id: 'enterEventExperienceV2',
      component: SplashEnterEventExperience,
      preview: {
        focus: 'splashPageStagePreview',
        targets: [],
        previews: ['splashPageStagePreview']
      }
    },
    {
      id: 'trackingPixelSnippetV2',
      component: TrackingPixelSnippet,
      preview: {
        focus: 'splashPageStagePreview',
        targets: [],
        previews: ['splashPageStagePreview']
      }
    }
  ]
};

export const brandKitSection: Section = {
  id: 'brandKit',
  label: 'Brand Kit',
  description: 'Select a brand kit for your event landing page and registration page.',
  modules: [
    {
      id: 'selectBrandKit',
      component: SelectBrandKitModule
    }
  ]
};

export const newStageSection: Section = {
  ...section3,
  modules: section3.modules.map(module =>
    module.id === 'stageFont' ? { ...module, component: NewFontModule } : module
  )
};

export const newSection4: Section = {
  ...section4,
  modules: section4.modules.map(module => (module.id === 'nameCard' ? { ...module, component: NameCardV2 } : module))
};

export default [section1, section2, section3, section4];
